import { useState, useEffect } from 'react';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'glass bg-white/95 shadow-xl backdrop-blur-xl' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center">
              <button 
                onClick={() => scrollToSection('hero')} 
                className={`text-3xl font-bold cursor-pointer whitespace-nowrap transition-all duration-300 ${
                  isScrolled ? 'text-gradient-animated' : 'text-white drop-shadow-lg'
                }`}
                style={{ fontFamily: 'Pacifico, cursive' }}
              >
                Le Bokal Givré
              </button>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <button 
                onClick={() => scrollToSection('brunch')} 
                className={`font-bold transition-all hover:scale-110 cursor-pointer whitespace-nowrap relative group ${
                  isScrolled ? 'text-[#4A7C59] hover:text-[#FF8F6B]' : 'text-white hover:text-[#FF8F6B]'
                }`}
              >
                Brunch
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#FF8F6B] group-hover:w-full transition-all duration-300"></span>
              </button>
              
              <button 
                onClick={() => scrollToSection('restaurant')} 
                className={`font-bold transition-all hover:scale-110 cursor-pointer whitespace-nowrap relative group ${
                  isScrolled ? 'text-[#4A7C59] hover:text-[#FF8F6B]' : 'text-white hover:text-[#FF8F6B]'
                }`}
              >
                Restaurant
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#FF8F6B] group-hover:w-full transition-all duration-300"></span>
              </button>
              
              <button 
                onClick={() => scrollToSection('bar')} 
                className={`font-bold transition-all hover:scale-110 cursor-pointer whitespace-nowrap relative group ${
                  isScrolled ? 'text-[#4A7C59] hover:text-[#FF8F6B]' : 'text-white hover:text-[#FF8F6B]'
                }`}
              >
                Bar
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#FF8F6B] group-hover:w-full transition-all duration-300"></span>
              </button>
              
              <button 
                onClick={() => scrollToSection('reservation')} 
                className="px-6 py-3 bg-gradient-to-r from-[#FF8F6B] to-[#ff6b47] text-white rounded-full font-bold hover:scale-110 transition-all cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl"
              >
                Réserver
              </button>
            </div>

            {/* Social Icons */}
            <div className="hidden md:flex items-center space-x-3">
              <a 
                href="https://www.instagram.com/lebokalgivre/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className={`w-11 h-11 flex items-center justify-center rounded-full transition-all hover:scale-110 cursor-pointer ${
                  isScrolled 
                    ? 'bg-gradient-to-br from-[#FF8F6B] to-[#ff6b47] text-white shadow-lg' 
                    : 'glass border-2 border-white text-white hover:bg-white hover:text-[#4A7C59]'
                }`}
              >
                <i className="ri-instagram-line text-xl"></i>
              </a>
              
              <a 
                href="https://www.facebook.com/100063594639194" 
                target="_blank" 
                rel="noopener noreferrer" 
                className={`w-11 h-11 flex items-center justify-center rounded-full transition-all hover:scale-110 cursor-pointer ${
                  isScrolled 
                    ? 'bg-[#4A7C59] text-white shadow-lg' 
                    : 'glass border-2 border-white text-white hover:bg-white hover:text-[#4A7C59]'
                }`}
              >
                <i className="ri-facebook-fill text-xl"></i>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden w-12 h-12 flex items-center justify-center rounded-full cursor-pointer transition-all ${
                isScrolled ? 'bg-[#4A7C59] text-white' : 'glass border-2 border-white text-white'
              }`}
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`fixed inset-0 bg-gradient-to-br from-[#4A7C59] via-[#3d6849] to-[#4A7C59] z-40 transition-all duration-500 ${
        isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
      } md:hidden`}>
        <div className="flex flex-col items-center justify-center h-full space-y-8 relative">
          {/* Decorative Elements */}
          <div className="absolute top-20 left-10 text-6xl opacity-20 animate-float">🌴</div>
          <div className="absolute bottom-20 right-10 text-6xl opacity-20 animate-float" style={{ animationDelay: '1s' }}>🍹</div>
          
          <button 
            onClick={() => scrollToSection('brunch')} 
            className="text-white text-4xl font-black hover:text-[#FF8F6B] hover:scale-110 transition-all cursor-pointer whitespace-nowrap"
          >
            Brunch
          </button>
          
          <button 
            onClick={() => scrollToSection('restaurant')} 
            className="text-white text-4xl font-black hover:text-[#FF8F6B] hover:scale-110 transition-all cursor-pointer whitespace-nowrap"
          >
            Restaurant
          </button>
          
          <button 
            onClick={() => scrollToSection('bar')} 
            className="text-white text-4xl font-black hover:text-[#FF8F6B] hover:scale-110 transition-all cursor-pointer whitespace-nowrap"
          >
            Bar
          </button>
          
          <button 
            onClick={() => scrollToSection('reservation')} 
            className="px-10 py-5 bg-gradient-to-r from-[#FF8F6B] to-[#ff6b47] text-white rounded-full text-2xl font-black hover:scale-110 transition-all cursor-pointer whitespace-nowrap shadow-2xl"
          >
            Réserver
          </button>
          
          <button 
            onClick={() => scrollToSection('contact')} 
            className="text-white text-4xl font-black hover:text-[#FF8F6B] hover:scale-110 transition-all cursor-pointer whitespace-nowrap"
          >
            Contact
          </button>
          
          <div className="flex space-x-6 mt-8">
            <a 
              href="https://www.instagram.com/lebokalgivre/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="w-16 h-16 flex items-center justify-center rounded-full bg-gradient-to-br from-[#FF8F6B] to-[#ff6b47] text-white hover:scale-110 transition-all cursor-pointer shadow-xl"
            >
              <i className="ri-instagram-line text-2xl"></i>
            </a>
            
            <a 
              href="https://www.facebook.com/100063594639194" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="w-16 h-16 flex items-center justify-center rounded-full bg-white text-[#4A7C59] hover:scale-110 transition-all cursor-pointer shadow-xl"
            >
              <i className="ri-facebook-fill text-2xl"></i>
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
